<template>
  <GeojsonLayer
    v-if="visible"
    :before="before"
    :layer="layer"
    :source="source"
    :sourceId="sourceId"
  />
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import { MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import {
  getInstallations,
  getAreasByInstallationId,
  getAreasStatsByInstallationId
} from '@/services/sensorData.service'
import {
  SET_CURRENT_HEATMAP_INTENSITY,
  SET_HEATMAP_DATE_ARRAY,
  SET_SELECTED_HEATMAP_DATE
} from '@/store/mutation-types'
export default {
  name: 'LayerHeatmapDynamic',
  components: {
    GeojsonLayer
  },
  props: {
    before: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      layerId: 'layer-heatmap-dynamic',
      sourceId: 'source-heatmap-dynamic',
      source: {
        type: 'geojson',
        data: {
          id: 'source-heatmap-dynamic',
          type: 'FeatureCollection',
          features: []
        }
      },
      installation: null,
      areas: null,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
      interval: null,
      areaStats: null,
      maxValue: null,
      minValue: null
    }
  },
  computed: {
    heatmapIntensity() {
      return this.currentHeatmapIntensity >= 0.5
        ? 0.025 * Math.exp(5 * this.currentHeatmapIntensity - 2.5)
        : 0.05 * this.currentHeatmapIntensity
    },
    heatmapRadius() {
      return this.currentHeatmapIntensity >= 0.5
        ? Math.exp(5 * this.currentHeatmapIntensity - 2.5)
        : 2 * this.currentHeatmapIntensity
    },
    heatmapWeight() {
      return this.currentHeatmapIntensity >= 0.5
        ? Math.exp(5 * this.currentHeatmapIntensity - 2.5)
        : 2 * this.currentHeatmapIntensity
    },
    layer() {
      return {
        id: this.layerId,
        type: 'heatmap',
        minzoom: 16,
        maxzoom: 24,
        paint: this.paint
      }
    },
    paint() {
      return {
        'heatmap-weight': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          [
            'match',
            ['get', 'value'],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
            0,
            [10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
            0,
            [20, 21, 22, 23, 24, 25, 26, 27, 28, 29],
            0,
            [30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
            0,
            [40, 41, 42, 43, 44, 45, 46, 47, 48, 49],
            0,
            [50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
            0,
            [60, 61, 62, 63, 64, 65, 66, 67, 68, 69],
            0,
            [70, 71, 72, 73, 74, 75, 76, 77, 78, 79],
            0,
            [80, 81, 82, 83, 84, 85, 86, 87, 88, 89],
            0,
            [90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100],
            0,
            0
          ],
          17,
          [
            'match',
            ['get', 'value'],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
            0.1 * this.heatmapWeight,
            [10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
            0.1 * this.heatmapWeight,
            [20, 21, 22, 23, 24, 25, 26, 27, 28, 29],
            0.2 * this.heatmapWeight,
            [30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
            0.3 * this.heatmapWeight,
            [40, 41, 42, 43, 44, 45, 46, 47, 48, 49],
            0.4 * this.heatmapWeight,
            [50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
            0.5 * this.heatmapWeight,
            [60, 61, 62, 63, 64, 65, 66, 67, 68, 69],
            0.6 * this.heatmapWeight,
            [70, 71, 72, 73, 74, 75, 76, 77, 78, 79],
            0.7 * this.heatmapWeight,
            [80, 81, 82, 83, 84, 85, 86, 87, 88, 89],
            0.8 * this.heatmapWeight,
            [90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100],
            0.9 * this.heatmapWeight,
            0.2 * this.heatmapWeight
          ],
          22.1,
          [
            'match',
            ['get', 'value'],
            [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
            this.heatmapWeight,
            [10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
            2 * this.heatmapWeight,
            [20, 21, 22, 23, 24, 25, 26, 27, 28, 29],
            3 * this.heatmapWeight,
            [30, 31, 32, 33, 34, 35, 36, 37, 38, 39],
            4 * this.heatmapWeight,
            [40, 41, 42, 43, 44, 45, 46, 47, 48, 49],
            5 * this.heatmapWeight,
            [50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
            6 * this.heatmapWeight,
            [60, 61, 62, 63, 64, 65, 66, 67, 68, 69],
            7 * this.heatmapWeight,
            [70, 71, 72, 73, 74, 75, 76, 77, 78, 79],
            8 * this.heatmapWeight,
            [80, 81, 82, 83, 84, 85, 86, 87, 88, 89],
            9 * this.heatmapWeight,
            [90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100],
            10 * this.heatmapWeight,
            2 * this.heatmapWeight
          ]
        ],
        'heatmap-intensity': this.heatmapIntensity,
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(0, 0, 255, 0)',
          0.1,
          'hsl(225, 61%, 58%)',
          0.4,
          'hsl(180, 78%, 49%)',
          0.6,
          'hsl(120, 83%, 51%)',
          0.8,
          'hsl(60, 84%, 49%)',
          1,
          'hsl(0, 91%, 48%)'
        ],
        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          17,
          this.heatmapRadius,
          22,
          this.heatmapRadius * 80
        ],
        'heatmap-opacity': 0.6
      }
    },
    visible() {
      return (
        this.isSensordataShowing &&
        this.isPlanviewShowing &&
        this.currentSensordataType === 'wifi' &&
        this.selectedBuilding.id === '879377092650749952' // zynka building
      )
    },
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding
    }),
    ...mapState(PLANVIEW_PREFIX, {
      currentHeatmapIntensity: (state) => state.currentHeatmapIntensity,
      currentSensordataType: (state) => state.currentSensordataType,
      isSensordataShowing: (state) => state.isSensordataShowing,
      selectedHeatmapDate: (state) => state.selectedHeatmapDate
    }),
    ...mapGetters({
      isPlanviewShowing: 'isPlanviewShowing',
      selectedLevelNumber: 'selectedLevelNumber'
    })
  },
  watch: {
    selectedHeatmapDate(n) {
      const newAreaStats = this.areaStats.find((el) => el.time === n)
      this.source.data.features = this.getFeaturesFromAreaStats(newAreaStats, this.areas)
    },
    selectedLevelNumber: function () {
      if (this.selectedBuilding.id !== '879377092650749952') return
      this.getHeatmap()
    }
  },
  created() {
    if (this.selectedBuilding.id !== '879377092650749952') return
    const date = new Date()
    date.setDate(date.getDate() - 1)
    this.startDate = date.toISOString().substring(0, 10)
    this.endDate = new Date().toISOString().substring(0, 10)
    this.startDate = '2020-05-25'
    this.endDate = '2020-05-25'
    this.startTime = '00:00:00'
    this.endTime = '23:00:00'
    this.interval = '1h'
    this.getSensorValues()
  },
  methods: {
    getFeaturesFromAreaStats(areaStats, areas) {
      this.maxValue = null
      this.minValue = null
      const features = []
      areaStats.areas.forEach((el) => {
        const area = areas.find((area) => area.id === el.id)
        if (area == null) return
        if (this.maxValue == null || this.maxValue < el.visitCount) this.maxValue = el.visitCount
        if (this.minValue == null || this.minValue > el.visitCount) this.minValue = el.visitCount
        const coordinates = [
          area.nodes[0].position.longitude,
          area.nodes[0].position.latitude,
          area.nodes[0].position.elevation
        ]
        const feature = {
          geometry: {
            coordinates: coordinates,
            type: 'Point'
          },
          id: area.id,
          properties: {
            macAdress: area.macAdress,
            visitCount: el.visitCount,
            value: 0
          },
          type: 'Feature'
        }
        features.push(feature)
      })
      const interval = this.maxValue - this.minValue
      features.forEach((el) => {
        if (interval === 0) el.properties.value = 0
        else el.properties.value = ((el.properties.visitCount - this.minValue) / interval) * 100
      })
      return features
    },
    async getSensorValues() {
      const installation = await this.getInstallations()
      if (installation == null) return
      this.installation = installation
      const areaData = await this.getAreas(this.installation.id)
      if (!areaData) return
      this.areas = areaData
      const areaStats = await getAreasStatsByInstallationId(
        this.installation.id,
        this.startDate,
        this.endDate,
        this.startTime,
        this.endTime,
        this.interval
      )
      this.areaStats = areaStats
      const timeArray = areaStats.map((el) => el.time)
      this.setHeatmapDateArray({ array: timeArray, date: this.startDate })
      this.setSelectedHeatmapDate({ date: timeArray[timeArray.length - 1] })
    },
    async getInstallations() {
      const data = await getInstallations()
      if (!data || !data.length) return null
      return data[0]
    },
    async getAreas(id) {
      const data = await getAreasByInstallationId(id)
      if (!data || !data.areas.length) return null
      return data.areas
    },
    ...mapMutations(PLANVIEW_PREFIX, {
      setCurrentHeatmapIntensity: SET_CURRENT_HEATMAP_INTENSITY,
      setHeatmapDateArray: SET_HEATMAP_DATE_ARRAY,
      setSelectedHeatmapDate: SET_SELECTED_HEATMAP_DATE
    })
  }
}
</script>

<style scoped></style>
